import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSEO } from '../lib/useSEO';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { sessionsAPI } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, CheckCircle, XCircle, Clock } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

// Timer constants
const ROUND_TIME_SECONDS = 10 * 60; // 10 minutes per round
const DAILY_DOUBLE_ANSWER_TIME = 20; // 20 seconds for Daily Double answers

export default function GamePlay() {
  const { sessionId } = useParams();
  useSEO('Live Game | JepPrep', 'Play a live multiplayer trivia game with friends in real-time.');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState('');
  const [wager, setWager] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const pollRef = useRef(null);
  
  // Timer state
  const [roundTimeLeft, setRoundTimeLeft] = useState(ROUND_TIME_SECONDS);
  const [ddTimeLeft, setDdTimeLeft] = useState(DAILY_DOUBLE_ANSWER_TIME);
  const [roundTimerActive, setRoundTimerActive] = useState(false);
  const [ddTimerActive, setDdTimerActive] = useState(false);
  const [roundExpired, setRoundExpired] = useState(false);
  const roundTimerRef = useRef(null);
  const ddTimerRef = useRef(null);

  useEffect(() => {
    loadSession();
    // Poll for state updates
    pollRef.current = setInterval(loadSession, 1000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (roundTimerRef.current) clearInterval(roundTimerRef.current);
      if (ddTimerRef.current) clearInterval(ddTimerRef.current);
    };
  }, [sessionId, loadSession]);

  // Start round timer when game starts
  useEffect(() => {
    if (session?.state?.phase && session.state.phase !== 'lobby' && !roundTimerActive && !roundExpired) {
      startRoundTimer();
    }
  }, [session?.state?.phase, roundTimerActive, roundExpired, startRoundTimer]);

  // Handle Daily Double timer
  useEffect(() => {
    const phase = session?.state?.phase;
    if (phase === 'daily_double_answering' && !ddTimerActive) {
      startDdTimer();
    } else if (phase !== 'daily_double_answering' && ddTimerActive) {
      stopDdTimer();
    }
  }, [session?.state?.phase, ddTimerActive, startDdTimer, stopDdTimer]);

  // Reset round timer when switching rounds
  useEffect(() => {
    const currentRound = session?.state?.current_round;
    if (currentRound === 'double_jeopardy' && roundTimeLeft <= 0) {
      // New round, reset timer
      setRoundTimeLeft(ROUND_TIME_SECONDS);
      setRoundExpired(false);
      startRoundTimer();
    }
  }, [session?.state?.current_round, roundTimeLeft, startRoundTimer]);

  const startRoundTimer = useCallback(() => {
    setRoundTimerActive(true);
    if (roundTimerRef.current) clearInterval(roundTimerRef.current);
    
    roundTimerRef.current = setInterval(() => {
      setRoundTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(roundTimerRef.current);
          setRoundExpired(true);
          setRoundTimerActive(false);
          toast.info('Time expired! Finish the current question.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const stopDdTimer = useCallback(() => {
    if (ddTimerRef.current) clearInterval(ddTimerRef.current);
    setDdTimerActive(false);
    setDdTimeLeft(DAILY_DOUBLE_ANSWER_TIME);
  }, []);

  const startDdTimer = useCallback(() => {
    setDdTimeLeft(DAILY_DOUBLE_ANSWER_TIME);
    setDdTimerActive(true);
    if (ddTimerRef.current) clearInterval(ddTimerRef.current);
    
    ddTimerRef.current = setInterval(() => {
      setDdTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(ddTimerRef.current);
          setDdTimerActive(false);
          submitAnswerFn();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [submitAnswerFn]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const lastResultRef = useRef(null);
  useEffect(() => { lastResultRef.current = lastResult; }, [lastResult]);

  const loadSession = useCallback(async () => {
    try {
      const response = await sessionsAPI.get(sessionId);
      setSession(response.data);
      
      // Clear result after showing
      if (lastResultRef.current) {
        setTimeout(() => setLastResult(null), 2000);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const submitAnswerFn = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await sessionsAPI.answer(sessionId, answer || '');
      setLastResult(response.data);
      setAnswer('');
      loadSession();
      
      // Check if round should end after this answer (timer expired)
      if (roundExpired) {
        toast.info('Round complete due to time!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit answer');
    } finally {
      setIsSubmitting(false);
    }
  }, [sessionId, answer, isSubmitting, loadSession, roundExpired]);

  // Alias for components and timer callbacks
  const submitAnswer = submitAnswerFn;

  const selectClue = async (categoryIdx, clueIdx) => {
    // Don't allow selecting new clues if round has expired
    if (roundExpired) {
      toast.error('Round time has expired!');
      return;
    }
    
    try {
      const response = await sessionsAPI.selectClue(sessionId, categoryIdx, clueIdx);
      if (response.data.success) {
        loadSession();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cannot select that clue');
    }
  };

  const handleBuzz = async () => {
    try {
      const response = await sessionsAPI.buzz(sessionId);
      if (response.data.accepted && response.data.is_first) {
        toast.success('You buzzed first!');
      } else if (!response.data.accepted) {
        toast.error(response.data.reason || 'Buzz not accepted');
      }
      loadSession();
    } catch (error) {
      toast.error('Failed to buzz');
    }
  };

  const activateBuzzer = async () => {
    try {
      await sessionsAPI.activateBuzzer(sessionId);
      loadSession();
    } catch (error) {
      toast.error('Failed to activate buzzer');
    }
  };

  const submitWager = async () => {
    if (isSubmitting || !wager) return;
    setIsSubmitting(true);
    try {
      await sessionsAPI.wager(sessionId, parseInt(wager));
      setWager('');
      loadSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid wager');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitFinalAnswer = async () => {
    if (isSubmitting || !answer.trim()) return;
    setIsSubmitting(true);
    try {
      const response = await sessionsAPI.finalAnswer(sessionId, answer);
      setAnswer('');
      if (response.data.success && response.data.results) {
        // Game over with results
        setLastResult({ final: true, ...response.data });
      }
      loadSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit answer');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || !session) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Loading game...</div>
      </div>
    );
  }

  const state = session.state || {};
  const phase = state.phase;
  const players = session.players || [];
  const currentClue = state.current_clue;
  const boardState = state.board_state || [];
  const categories = session.board?.categories || [];
  const isHost = session.host_id === user?.id;
  const isSelectingPlayer = state.selecting_player_id === user?.id;
  const isAnsweringPlayer = state.answering_player_id === user?.id;
  const currentRound = state.current_round || 'jeopardy';

  // Get clue values based on round
  const getClueValue = (row) => {
    const baseValues = [100, 200, 300, 400, 500];
    return currentRound === 'double_jeopardy' ? baseValues[row] * 2 : baseValues[row];
  };

  // Get round display name with logo
  const getRoundDisplay = () => {
    if (currentRound === 'double_jeopardy') {
      return (
        <div className="flex items-center gap-2">
          <span className="text-[hsl(var(--brand-gold-500))]">Double</span>
          <img src={LOGO_URL} alt="JepPrep" className="h-8" />
        </div>
      );
    } else if (currentRound === 'final_jeopardy') {
      return (
        <div className="flex items-center gap-2">
          <span className="text-[hsl(var(--brand-gold-500))]">Final</span>
          <img src={LOGO_URL} alt="JepPrep" className="h-8" />
        </div>
      );
    } else {
      return <img src={LOGO_URL} alt="JepPrep" className="h-8" />;
    }
  };

  // Render round timer
  const renderRoundTimer = () => {
    const isLow = roundTimeLeft <= 60;
    const isCritical = roundTimeLeft <= 30;
    
    return (
      <div className={`flex items-center gap-2 font-mono text-lg ${isCritical ? 'text-red-500' : isLow ? 'text-yellow-500' : 'text-white'}`}>
        <Clock className="w-5 h-5" />
        <span>{formatTime(roundTimeLeft)}</span>
      </div>
    );
  };

  // Render game board
  const renderBoard = () => (
    <div className="space-y-2">
      {/* Category Headers */}
      <div className="grid grid-cols-6 gap-1 sm:gap-2">
        {categories.map((cat, idx) => (
          <div key={cat.name || `cat-${idx}`} className="category-header text-[10px] sm:text-sm">
            {cat.name}
          </div>
        ))}
      </div>
      
      {/* Clue Grid */}
      {[0, 1, 2, 3, 4].map((row) => (
        <div key={row} className="grid grid-cols-6 gap-1 sm:gap-2">
          {categories.map((cat, catIdx) => {
            const isRevealed = boardState[catIdx]?.[row] || false;
            const canSelect = isSelectingPlayer && !isRevealed && !roundExpired;
            return (
              <button
                key={catIdx}
                onClick={() => canSelect && selectClue(catIdx, row)}
                disabled={!canSelect}
                className={`board-cell text-xs sm:text-lg ${isRevealed ? 'revealed' : ''} ${canSelect ? 'cursor-pointer' : 'cursor-default'} ${roundExpired && !isRevealed ? 'opacity-50' : ''}`}
                data-testid={`clue-${catIdx}-${row}`}
              >
                {isRevealed ? '' : `$${getClueValue(row)}`}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );

  // Render player scores
  const renderScores = () => (
    <div className="flex justify-center gap-4 mb-4">
      {players.map((player) => (
        <div key={player.player_id} className="text-center">
          <div className="text-sm text-gray-400">{player.display_name}</div>
          <div className={`font-mono text-xl ${player.score >= 0 ? 'score-positive' : 'score-negative'}`}>
            ${player.score.toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );

  // Render Daily Double timer bar
  const renderDdTimer = () => {
    const percentage = (ddTimeLeft / DAILY_DOUBLE_ANSWER_TIME) * 100;
    const isLow = ddTimeLeft <= 5;
    
    return (
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-400">Time remaining</span>
          <span className={`font-mono ${isLow ? 'text-red-500' : 'text-white'}`}>{ddTimeLeft}s</span>
        </div>
        <div className="h-2 bg-[hsl(var(--accent))] rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-200 ${isLow ? 'bg-red-500' : 'bg-[hsl(var(--brand-gold-500))]'}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  // Game Over Screen
  if (phase === 'game_over' || lastResult?.final) {
    const results = lastResult?.results || players.sort((a, b) => b.score - a.score);
    const winner = results[0];
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-8 text-center">
            <Trophy className="w-20 h-20 mx-auto text-[hsl(var(--brand-gold-500))] mb-4" />
            <h1 className="font-display font-bold text-3xl text-white mb-2">Game Over!</h1>
            <p className="text-[hsl(var(--brand-gold-400))] text-xl mb-6">
              Winner: {winner?.display_name || winner?.player_id}
            </p>
            <div className="space-y-2 mb-6">
              {results.map((p, idx) => (
                <div key={p.player_id || idx} className="flex justify-between p-3 bg-[hsl(var(--accent))] rounded-lg">
                  <span className="text-white">{idx + 1}. {p.display_name || p.player_id}</span>
                  <span className={`font-mono ${(p.final_score || p.score) >= 0 ? 'text-[hsl(var(--brand-gold-500))]' : 'text-red-500'}`}>
                    ${(p.final_score || p.score).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
            <Link to="/dashboard">
              <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-2 sm:p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header with Logo and Timer */}
        <div className="flex justify-between items-center mb-4">
          {getRoundDisplay()}
          <div className="flex items-center gap-4">
            {renderRoundTimer()}
            <div className="text-gray-400 text-sm">PIN: {session.pin}</div>
          </div>
        </div>

        {/* Scores */}
        {renderScores()}

        {/* Round Expired Notice */}
        {roundExpired && phase === 'selecting_clue' && (
          <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4 mb-4 text-center">
            <p className="text-yellow-500 font-semibold">Time has expired for this round!</p>
            <p className="text-gray-400 text-sm">Moving to the next round...</p>
          </div>
        )}

        {/* Main Game Area */}
        <AnimatePresence mode="wait">
          {/* Selecting Clue Phase */}
          {phase === 'selecting_clue' && (
            <motion.div
              key="board"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <p className="text-center text-gray-400 mb-4">
                {roundExpired 
                  ? 'Round time expired' 
                  : isSelectingPlayer 
                    ? 'Select a clue from the board' 
                    : `Waiting for ${players.find(p => p.player_id === state.selecting_player_id)?.display_name || 'player'} to select...`}
              </p>
              {renderBoard()}
            </motion.div>
          )}

          {/* Clue Displayed - Waiting for buzzer activation */}
          {phase === 'clue_displayed' && currentClue && (
            <motion.div
              key="clue-display"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <Card className="clue-display mb-6">
                <div className="text-[hsl(var(--brand-gold-500))] text-sm font-semibold mb-4">
                  {currentClue.category_name} - ${currentClue.value}
                </div>
                <div className="text-white text-xl md:text-2xl" data-testid="current-clue">
                  {currentClue.question}
                </div>
              </Card>
              {isHost && (
                <Button onClick={activateBuzzer} className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-xl px-8 py-6">
                  Activate Buzzer
                </Button>
              )}
            </motion.div>
          )}

          {/* Buzzer Active */}
          {phase === 'buzzer_active' && currentClue && (
            <motion.div
              key="buzzer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <Card className="clue-display mb-6">
                <div className="text-[hsl(var(--brand-gold-500))] text-sm font-semibold mb-4">
                  {currentClue.category_name} - ${currentClue.value}
                </div>
                <div className="text-white text-xl md:text-2xl">{currentClue.question}</div>
              </Card>
              <button
                onClick={handleBuzz}
                className="buzzer-button"
                data-testid="buzz-button"
              >
                BUZZ IN!
              </button>
            </motion.div>
          )}

          {/* Answering Phase */}
          {phase === 'answering' && currentClue && (
            <motion.div
              key="answering"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <Card className="clue-display mb-6">
                <div className="text-[hsl(var(--brand-gold-500))] text-sm font-semibold mb-4">
                  {currentClue.category_name} - ${currentClue.value}
                </div>
                <div className="text-white text-xl md:text-2xl mb-4">{currentClue.question}</div>
                {isAnsweringPlayer ? (
                  <div className="text-green-400 text-lg">Your turn to answer!</div>
                ) : (
                  <div className="text-gray-400">Waiting for {players.find(p => p.player_id === state.answering_player_id)?.display_name} to answer...</div>
                )}
              </Card>
              
              {isAnsweringPlayer && (
                <div className="flex gap-3 max-w-lg mx-auto">
                  <Input
                    type="text"
                    placeholder="What is..."
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && submitAnswer()}
                    autoFocus
                    className="flex-1 bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-lg py-6"
                    data-testid="answer-input"
                  />
                  <Button
                    onClick={submitAnswer}
                    disabled={isSubmitting || !answer.trim()}
                    className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] px-8"
                    data-testid="submit-answer-button"
                  >
                    Submit
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* Daily Double Wager */}
          {phase === 'daily_double_wager' && isAnsweringPlayer && (
            <motion.div
              key="dd-wager"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="text-4xl font-display text-[hsl(var(--brand-gold-500))] mb-6 daily-double-reveal">DAILY DOUBLE!</div>
              <Card className="max-w-md mx-auto bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardContent className="p-6">
                  <p className="text-white mb-4">Enter your wager:</p>
                  <Input
                    type="number"
                    placeholder="$0"
                    value={wager}
                    onChange={(e) => setWager(e.target.value)}
                    className="wager-input mb-4"
                    data-testid="wager-input"
                  />
                  <Button
                    onClick={submitWager}
                    disabled={isSubmitting || !wager}
                    className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]"
                  >
                    Make It a True Daily Double!
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Daily Double Answering - With 20 second timer */}
          {phase === 'daily_double_answering' && currentClue && (
            <motion.div
              key="dd-answer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <Card className="clue-display mb-6">
                {/* Daily Double Timer */}
                {renderDdTimer()}
                
                <div className="text-[hsl(var(--brand-gold-500))] text-sm font-semibold mb-4">
                  Daily Double - ${state.daily_double_wager}
                </div>
                <div className="text-white text-xl md:text-2xl">{currentClue.question}</div>
              </Card>
              
              {isAnsweringPlayer && (
                <div className="flex gap-3 max-w-lg mx-auto">
                  <Input
                    type="text"
                    placeholder="What is..."
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && submitAnswer()}
                    autoFocus
                    className="flex-1 bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-lg py-6"
                  />
                  <Button
                    onClick={submitAnswer}
                    disabled={isSubmitting || !answer.trim()}
                    className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] px-8"
                  >
                    Submit
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* Final Jeopardy Wagering */}
          {phase === 'final_jeopardy_wagering' && (
            <motion.div
              key="fj-wager"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-3xl font-display text-[hsl(var(--brand-gold-500))]">Final</span>
                <img src={LOGO_URL} alt="JepPrep" className="h-12" />
              </div>
              <p className="text-white text-xl mb-6">Category: {session.board?.final_jeopardy?.category}</p>
              
              {!state.final_jeopardy_wagers?.[user?.id] ? (
                <Card className="max-w-md mx-auto bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                  <CardContent className="p-6">
                    <p className="text-white mb-4">Enter your wager (max: ${Math.max(0, players.find(p => p.player_id === user?.id)?.score || 0)}):</p>
                    <Input
                      type="number"
                      placeholder="$0"
                      value={wager}
                      onChange={(e) => setWager(e.target.value)}
                      className="wager-input mb-4"
                    />
                    <Button
                      onClick={submitWager}
                      disabled={isSubmitting || !wager}
                      className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]"
                    >
                      Submit Wager
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <p className="text-gray-400">Waiting for other players to wager...</p>
              )}
            </motion.div>
          )}

          {/* Final Jeopardy Answering */}
          {phase === 'final_jeopardy_answering' && (
            <motion.div
              key="fj-answer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <Card className="clue-display mb-6">
                <div className="text-[hsl(var(--brand-gold-500))] text-sm font-semibold mb-4">
                  Final JepPrep - {session.board?.final_jeopardy?.category}
                </div>
                <div className="text-white text-xl md:text-2xl">{session.board?.final_jeopardy?.question}</div>
              </Card>
              
              {!state.final_jeopardy_answers?.[user?.id] ? (
                <div className="flex gap-3 max-w-lg mx-auto">
                  <Input
                    type="text"
                    placeholder="What is..."
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && submitFinalAnswer()}
                    autoFocus
                    className="flex-1 bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-lg py-6"
                  />
                  <Button
                    onClick={submitFinalAnswer}
                    disabled={isSubmitting || !answer.trim()}
                    className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] px-8"
                  >
                    Final Answer
                  </Button>
                </div>
              ) : (
                <p className="text-gray-400">Waiting for other players to answer...</p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Result Toast */}
        <AnimatePresence>
          {lastResult && !lastResult.final && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50"
            >
              <Card className={`${lastResult.is_correct ? 'bg-green-500/90' : 'bg-red-500/90'} border-none`}>
                <CardContent className="p-4 flex items-center gap-3">
                  {lastResult.is_correct ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    <XCircle className="w-6 h-6" />
                  )}
                  <span className="text-white font-semibold">
                    {lastResult.is_correct ? `+$${Math.abs(lastResult.points_change)}` : `-$${Math.abs(lastResult.points_change)}`}
                  </span>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
